import React, { useEffect, useState, useRef } from 'react'
import Member from 'components/teams/Member'
import useStore from 'state/knovStore'
import CreatableSelect from 'react-select/creatable'
import api from '../../api/api'
import cn from 'classnames'
import styles from 'components/teams/team-settings.module.scss'
import WithTooltip from 'components/shared/WithTooltip'
import ReactTooltip from 'react-tooltip'
import { isMobile } from 'react-device-detect'
import { getTooltipRef } from 'state/imperativeApis/tooltipApi'
import TeamLogo from 'components/shared/icons/TeamLogo'
import SettingsHeader from 'components/shared/settings/SettingsHeader'
import Button from 'components/shared/buttons/Button'
import { PurpleButton } from 'components/shared/buttons/Button'
import TeamBannerCropper from './TeamBannerCropper'
import TeamLogoCropper from './TeamLogoCropper'
import StreamNotificationOptions from './StreamNotificationOptions'

const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

export default function Team(props) {
    const [team, setTeam] = useState(props.team)
    const closeModal = props.closeModal
    const activeSpaceId = useStore(state => state.activeSpaceId)

    useEffect(
        function getTeam() {
            // Always fetch the full team data to get members and invitations
            const teamId = props.teamId || props.team?.id
            if (teamId) {
                setTeamLoading(true)
                api.getTeam(teamId)
                    .then(team => {
                        setTeam(team)
                        setTeamName(team.name)
                        setBannerLoading(true)
                        setTeamLoading(false)
                    })
                    .catch(error => {
                        console.error('Error fetching team:', error)
                        setTeamLoading(false)
                    })
            }
        },
        [props.teamId, props.team?.id],
    )

    const [selectedOptions, setSelectedOptions] = useState([])
    const [teamName, setTeamName] = useState(props.team?.name)
    const [showBannerCropper, setShowBannerCropper] = useState(false)
    const [selectedBannerImage, setSelectedBannerImage] = useState(null)
    const [bannerKey, setBannerKey] = useState(Date.now())
    const [bannerLoading, setBannerLoading] = useState(!!props.team?.id)
    const [bannerError, setBannerError] = useState(false)
    const [teamLoading, setTeamLoading] = useState(true)
    const [showLogoCropper, setShowLogoCropper] = useState(false)
    const [selectedLogoImage, setSelectedLogoImage] = useState(null)
    const [logoKey, setLogoKey] = useState(Date.now())
    const bannerInputRef = useRef(null)
    const logoInputRef = useRef(null)

    const [users, setUsers] = useState(null)
    useEffect(
        function getUsers() {
            if (team)
                api.getUsers({ space_id: team.space_id }).then(users => {
                    setUsers(users)
                })
        },
        [team],
    )

    const tooltipRef = getTooltipRef()

    const handleChange = selectedOption => {
        setSelectedOptions(selectedOption)
    }

    const filterOption = (option, inputValue) => {
        const { value } = option
        return value.toLowerCase().startsWith(inputValue.toLowerCase())
    }

    const handleSubmit = e => {
        e.preventDefault()
        setSelectedOptions(null)
        api.addToTeam(
            {
                emails: selectedOptions.filter(o => o?.__isNew__).map(o => o?.value),
                ids: selectedOptions
                    .filter(o => !o?.__isNew__)
                    .map(a => users.find(u => u?.name === a?.value)?.id),
            },
            team.id,
        ).then(data => {
            setTeam(data.team)
        })
    }

    const handleDeleteMember = async userId => {
        const apiTeam = await api.removeFromTeam(team.id, userId)
        setTeam(apiTeam)
    }

    const handleDeleteInvite = async email => {
        const apiTeam = await api.deleteInvite(email, team.id)
        if (apiTeam) {
            setTeam(apiTeam)
        }
    }

    const handleNotiOption = async period => {
        const apiTeam = await api.updateTeam(team.id, { notify_period: period })
        if (apiTeam) {
            setTeam(apiTeam)
        }
    }

    const handleBannerSelect = e => {
        const file = e.target.files[0]
        if (file && file.size > 10 * 1024 * 1024) {
            // 10MB limit
            alert('File too large. Please select an image under 10MB.')
            e.target.value = '' // Reset input
            return
        }

        if (file) {
            const reader = new FileReader()
            reader.onload = e => {
                setSelectedBannerImage(e.target.result)
                setShowBannerCropper(true)
            }
            reader.readAsDataURL(file)
        }
        // Reset the input so the same file can be selected again
        e.target.value = ''
    }

    const handleBannerSave = async croppedBlob => {
        const formData = new FormData()
        formData.append('banner_image', croppedBlob, 'banner.png')

        try {
            await api.updateTeamBanner(team.id, formData)
            // Refetch the team to get all data including the new banner
            const freshTeam = await api.getTeam(team.id)
            setTeam(freshTeam)
            setShowBannerCropper(false)

            // Force re-render of banner image
            setBannerKey(Date.now())
            setBannerLoading(true)
            setBannerError(false)

            // Force refresh by updating the team in the store
            try {
                // The SearchHeader will refresh when the modal closes
            } catch (err) {
                console.log('Could not refresh streams:', err)
            }
        } catch (error) {
            console.error('Error updating banner:', error)
        }
    }

    const handleLogoSelect = e => {
        const file = e.target.files[0]
        if (file && file.size > 5 * 1024 * 1024) {
            // 5MB limit for logos
            alert('File too large. Please select an image under 5MB.')
            e.target.value = '' // Reset input
            return
        }

        if (file) {
            const reader = new FileReader()
            reader.onload = e => {
                setSelectedLogoImage(e.target.result)
                setShowLogoCropper(true)
            }
            reader.readAsDataURL(file)
        }
        // Reset the input so the same file can be selected again
        e.target.value = ''
    }

    const handleLogoSave = async croppedBlob => {
        const formData = new FormData()
        formData.append('team_image', croppedBlob, 'logo.png')

        try {
            const updatedTeam = await api.updateTeam(team.id, formData)
            setTeam(updatedTeam)
            setShowLogoCropper(false)

            // Force re-render of logo
            setLogoKey(Date.now())

            // Update the team in the store
            const teams = await api.getTeams({ space_id: activeSpaceId })
            useStore.getState().set({ userStreams: teams })
        } catch (error) {
            console.error('Error updating logo:', error)
        }
    }

    const memberListComponent = team?.users?.map(user => {
        return (
            <Member
                key={user.id}
                shouldHandleDelete={gon.currentUser.id == team.admin_id && user.id != team.admin_id}
                handleDelete={() => handleDeleteMember(user.id)}
                content={`@${user.name}`}
            />
        )
    })

    const invitationListComponent = team?.invitations?.map(user => {
        return (
            <Member
                key={user.id}
                content={user.email}
                shouldHandleDelete={true}
                handleDelete={() => handleDeleteInvite(user.email)}
            />
        )
    })

    const instantAppStyles = team?.notify_period === 'instant_app' ? styles.chosenNoti : null
    const instantEmailStyles = team?.notify_period === 'instant_email' ? styles.chosenNoti : null
    const oneDayStyles =
        team?.notify_period === 'one_day_email' || team?.notify_period === null
            ? styles.chosenNoti
            : null
    const threeDayStyles = team?.notify_period === 'three_day_email' ? styles.chosenNoti : null
    const oneWeekStyles = team?.notify_period === 'one_week_email' ? styles.chosenNoti : null
    const showSaveBtn = teamName !== team?.name

    if (teamLoading) {
        return (
            <div className={cn(styles.teamSettingsContainer, closeModal && styles.modalContainer)}>
                <div className={cn(styles.teamSettingsComp, styles.loadingContainer)}>
                    <i className="fa fa-spinner fa-spin fa-2x" />
                    <p>Loading stream settings...</p>
                </div>
            </div>
        )
    }

    return (
        <div className={cn(styles.teamSettingsContainer, closeModal && styles.modalContainer)}>
            <div
                className={cn(
                    'team-comp',
                    styles.teamSettingsComp,
                    isMobile && styles.mobileTeamSettingsComp,
                    closeModal && styles.modalView,
                )}
                style={{ position: 'relative' }}
            >
                <div className={styles.settingsHeaderContainer}>
                    {team && (
                        <SettingsHeader
                            header="Stream Settings"
                            name={team?.name}
                            IconComponent={
                                team ? (
                                    <TeamLogo
                                        key={logoKey}
                                        team={team}
                                        contextStyles={styles.bigLogo}
                                        initialStyles={cn(styles.initialStyles, styles.bigInitials)}
                                    />
                                ) : null
                            }
                            openImageUpdateModal={() => logoInputRef.current.click()}
                            handleSave={async name => {
                                const apiTeam = await api.updateTeam(team.id, { name })
                                setTeam(apiTeam)
                            }}
                        />
                    )}
                </div>

                <div className={styles.bannerSection}>
                    <h5>Team Banner</h5>
                    <div
                        className={styles.bannerPreview}
                        onClick={() => bannerInputRef.current.click()}
                    >
                        {team?.id ? (
                            <>
                                <img
                                    key={bannerKey}
                                    src={`${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=medium&t=${bannerKey}`}
                                    onError={e => {
                                        setBannerLoading(false)
                                        setBannerError(true)
                                        e.target.style.display = 'none'
                                    }}
                                    onLoad={e => {
                                        setBannerLoading(false)
                                        setBannerError(false)
                                        e.target.style.display = 'block'
                                    }}
                                    className={styles.bannerImage}
                                    style={{
                                        display: bannerLoading || bannerError ? 'none' : 'block',
                                    }}
                                />
                                {/* Always show skeleton while loading */}
                                {bannerLoading && <div className={styles.bannerSkeleton} />}
                                {/* Show placeholder on error or if no banner exists */}
                                {!bannerLoading && bannerError && (
                                    <div
                                        className={styles.bannerPlaceholder}
                                        style={{ display: 'flex' }}
                                    >
                                        <span>Click to upload banner</span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles.bannerPlaceholder} style={{ display: 'flex' }}>
                                <span>Click to upload banner</span>
                            </div>
                        )}
                        <div className={styles.editBannerButton}>
                            <i className="fa fa-pencil" />
                        </div>
                    </div>

                    <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleBannerSelect}
                        style={{ display: 'none' }}
                    />
                </div>

                <StreamNotificationOptions
                    notifyPeriod={team?.notify_period}
                    onChange={handleNotiOption}
                />

                <h5>Invite new team member (type email or username then tab):</h5>
                <div>
                    <form className="invite-form" onSubmit={handleSubmit}>
                        <CreatableSelect
                            isMulti
                            value={selectedOptions}
                            onChange={handleChange}
                            placeholder={
                                users
                                    ? 'Enter a username or email address and hit tab...'
                                    : 'Loading...'
                            }
                            filterOption={filterOption}
                            formatCreateLabel={v =>
                                emailRegex.test(v)
                                    ? `Invite "${v}" by email`
                                    : 'Please enter a valid Knovigator username or email address'
                            }
                            options={users
                                ?.filter(u => !team?.users?.map(tu => tu?.id).includes(u?.id))
                                .filter(u => u?.id != gon?.currentUser?.id)
                                .map(u => ({ value: u?.name, label: u?.name }))}
                        />

                        <div className={styles.inviteBtnContainer}>
                            <button
                                type="submit"
                                style={{ border: 'none', background: 'none', padding: 0 }}
                            >
                                <PurpleButton text="Invite" />
                            </button>
                        </div>
                    </form>
                </div>

                <h5>Outstanding email invites: {team?.invitations?.length > 0 ? '' : 'None'}</h5>
                <div className="user-tag-container">{invitationListComponent}</div>

                <h5>Team members:</h5>
                <div className="user-tag-container">{memberListComponent}</div>

                <ReactTooltip
                    ref={tooltip => (tooltipRef.current = tooltip)}
                    effect="solid"
                    place="bottom"
                    delayShow={1000}
                    arrowColor="rgba(0,0,0,0)"
                    globalEventOff={isMobile ? 'touchstart' : undefined}
                />
            </div>

            {showBannerCropper && (
                <TeamBannerCropper
                    image={selectedBannerImage}
                    onSave={handleBannerSave}
                    onCancel={() => setShowBannerCropper(false)}
                />
            )}

            {showLogoCropper && (
                <TeamLogoCropper
                    image={selectedLogoImage}
                    onSave={handleLogoSave}
                    onCancel={() => setShowLogoCropper(false)}
                />
            )}

            <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoSelect}
                style={{ display: 'none' }}
            />
        </div>
    )
}
